import { apiClient } from '../apiClient';
import type {
	TSyteLineCustomer,
	TDealerInventoryUnitRow,
	TSyteLineInvoice,
	TSyteLineOrder,
	TUnitUpdateReturn,
} from './types';

export type TDealerInventoryResponse = Record<string, unknown>;

export function getDealerPortalCustomer(customerNumber: string): Promise<TSyteLineCustomer> {
	const q = encodeURIComponent(customerNumber.trim());
	return apiClient.get<TSyteLineCustomer>(`/Customer?customerNumber=${q}`);
}

/** Partial name or customer number search (Fusion API: GET DealerInventory/customerSearch). */
export function searchDealerPortalCustomers(search: string): Promise<TSyteLineCustomer[]> {
	const q = encodeURIComponent(search.trim());
	return apiClient.get<TSyteLineCustomer[]>(`/DealerInventory/customerSearch?search=${q}`);
}

export function getDealerPortalInventory(customerNumber: string): Promise<TDealerInventoryResponse> {
	const q = encodeURIComponent(customerNumber.trim());
	return apiClient.get<TDealerInventoryResponse>(`/DealerInventory?customerNumber=${q}`);
}

/** True when soldDate should be sent (omit blank and SyteLine null-date sentinels from the request). */
function isSoldDateToSend(soldDate: string | undefined): soldDate is string {
	if (soldDate == null || String(soldDate).trim() === '') return false;
	const d = String(soldDate).trim().slice(0, 10);
	if (d === '0001-01-01' || d === '1900-01-01') return false;
	return true;
}

/** POST DealerInventory — query params match FusionAPI DealerInventoryController.Post. `soldDate` omitted when unset/sentinel. */
export function updateDealerPortalUnit(params: {
	customerNumber: string;
	serialNumber: string;
	soldDate?: string;
	rental: boolean;
}): Promise<TUnitUpdateReturn> {
	const qs = new URLSearchParams();
	qs.set('customerNumber', params.customerNumber.trim());
	qs.set('serialNumber', params.serialNumber.trim());
	qs.set('rental', params.rental ? 'true' : 'false');
	if (isSoldDateToSend(params.soldDate)) {
		qs.set('soldDate', params.soldDate.trim());
	}
	return apiClient.post<TUnitUpdateReturn>(`/DealerInventory?${qs.toString()}`, undefined);
}

export function getDealerPortalOrders(customerNumber: string): Promise<TSyteLineOrder[]> {
	const q = encodeURIComponent(customerNumber.trim());
	return apiClient.get<TSyteLineOrder[]>(`/Order?customerNumber=${q}`);
}

export function getDealerPortalOrderDetail(
	orderNumber: string,
	customerNumber: string,
): Promise<TSyteLineOrder> {
	const o = encodeURIComponent(orderNumber.trim());
	const c = encodeURIComponent(customerNumber.trim());
	return apiClient.get<TSyteLineOrder>(`/OrderDetail?orderNumber=${o}&customerNumber=${c}`);
}

export type TGetDealerPortalInvoicesParams = {
	customerNumber: string;
	invNumber?: string | null;
	orderNumber?: string | null;
	customerPO?: string | null;
};

export function getDealerPortalInvoices(params: TGetDealerPortalInvoicesParams): Promise<TSyteLineInvoice[]> {
	const qs = new URLSearchParams();
	qs.set('customerNumber', params.customerNumber.trim());
	if (params.invNumber?.trim()) qs.set('invNumber', params.invNumber.trim());
	if (params.orderNumber?.trim()) qs.set('orderNumber', params.orderNumber.trim());
	if (params.customerPO?.trim()) qs.set('customerPO', params.customerPO.trim());
	return apiClient.get<TSyteLineInvoice[]>(`/Invoice?${qs.toString()}`);
}

export function getDealerPortalInvoiceDetail(
	invoiceNumber: string,
	customerNumber: string,
): Promise<TSyteLineInvoice> {
	const inv = encodeURIComponent(invoiceNumber.trim());
	const c = encodeURIComponent(customerNumber.trim());
	return apiClient.get<TSyteLineInvoice>(`/InvoiceDetail?invoiceNumber=${inv}&customerNumber=${c}`);
}

export function parseInventoryTables(
	payload: TDealerInventoryResponse,
): { key: string; rows: TDealerInventoryUnitRow[] }[] {
	const root = unwrapInventoryPayload(payload);
	const skipKeys = new Set(['errorMessage', 'errors', 'message']);

	const arrayEntries = Object.entries(root).filter(([key, value]) => {
		if (skipKeys.has(key)) return false;
		return Array.isArray(value);
	});

	const sorted = [...arrayEntries].sort(([a], [b]) => {
		const rank = (key: string) => {
			const k = key.toLowerCase();
			if (k === 'onhandunits') return 0;
			if (k === 'unitspendingregistrations') return 1;
			if (k === 'registredunits' || k === 'registeredunits') return 2;
			return 100;
		};
		const ra = rank(a);
		const rb = rank(b);
		if (ra !== rb) return ra - rb;
		return a.localeCompare(b);
	});

	return sorted.slice(0, 3).map(([key, value]) => ({
		key,
		rows: (value as unknown[]).filter(
			(r) => r && typeof r === 'object' && !Array.isArray(r),
		) as TDealerInventoryUnitRow[],
	}));
}

function unwrapInventoryPayload(raw: TDealerInventoryResponse): Record<string, unknown> {
	const data = raw?.data;
	if (data && typeof data === 'object' && !Array.isArray(data)) {
		return data as Record<string, unknown>;
	}
	return raw;
}
